/**
 * Cached Intl formatters, and the handful of formats the app actually prints.
 *
 * WHY THIS EXISTS
 * `n.toLocaleString()` and `d.toLocaleDateString(…, opts)` each build a fresh
 * Intl formatter — resolving the locale and compiling the pattern — on every
 * call, and they run in every row and tile. The tree held 113 of them; a
 * 400-unit availability table re-rendered on a keystroke built well over a
 * thousand formatters to print the same few patterns. PropPay measured the
 * same shape at ~25,000 formatters per render on its deposit table and fixed it
 * this way. A cached formatter prints identical output and costs nothing to
 * reuse.
 *
 * One formatter per (locale, options), built on first use and kept for the
 * life of the page. Keyed by the options' JSON, so a call site may keep passing
 * an inline literal: `dateFormat({ month: "short", day: "numeric" })`.
 *
 * Everything here that renders a value accepts what the value ACTUALLY is —
 * null, undefined, a numeric string from a JSON API — and returns the em dash
 * for anything it cannot print. An em dash, never a zero: "0" is a claim about
 * the data, "—" says the figure is not known (see lib/formatNumber.ts, which
 * stays for its callers; new code formats through here).
 */

import { DEFAULT_PROJECT_TIME_ZONE, PLATFORM_TIME_ZONE, timeZoneLabel, zonedYmd } from "@shared/timeZones";

/** The app's locale. SaleFish is a Canadian product: "$1,249,900", "Sep 4, 2026", "2:15 p.m.". */
export const LOCALE = "en-CA";
/**
 * SaleFish's own clock, for what belongs to the platform rather than a project:
 * platform reports, usage, the user log, billing, the competitor and translation
 * refreshes. A project's own times use the display zone below.
 */
export { PLATFORM_TIME_ZONE };
/**
 * The locale numbers, money, dates and times are SHOWN in. It follows the
 * reader's language — LanguageProvider sets it — so a French reader sees
 * "4 sept. 2026" and "1 249 900 $". The "numeric" date style and torontoYmd
 * stay on LOCALE, and so does anything built inside `inMachineLocale()` (CSV
 * exports): those are read back, compared or re-imported, and a decimal comma
 * or a narrow no-break space would break them. Inputs never format through
 * these helpers.
 */
let displayLocale = LOCALE;
export function setDisplayLocale(locale: string): void {
  displayLocale = locale;
}

/**
 * Run `fn` with every formatter here printing en-CA — for a file the reader
 * downloads and may import again. Synchronous, so nothing else renders between.
 */
export function inMachineLocale<T>(fn: () => T): T {
  const was = displayLocale;
  displayLocale = LOCALE;
  try {
    return fn();
  } finally {
    displayLocale = was;
  }
}
export function getDisplayLocale(): string {
  return displayLocale;
}

/**
 * The currency a project's figures are in: US dollars on a Florida or Texas
 * project, Canadian everywhere else. Set from the open project (AppShell,
 * `useProjectCurrencySync`), as the locale is set from the reader's language.
 * Shown as "$" either way: the project already says which dollar.
 */
let displayCurrency: "CAD" | "USD" = "CAD";
export function setDisplayCurrency(currency: "CAD" | "USD"): void {
  displayCurrency = currency;
}
export function getDisplayCurrency(): "CAD" | "USD" {
  return displayCurrency;
}
/**
 * The zone a project's own times are shown in: the open project's, from its
 * province or state (shared/timeZones.ts) — Central Time on a Texas project,
 * Toronto's on an Ontario one, and Toronto outside any project. Set from the
 * open project by AppShell (`useProjectTimeZoneSync`), as the currency is.
 *
 * Only call sites that pin a zone read it. A formatter given no `timeZone`
 * still prints in the reader's own zone, which is the device's clock: that is
 * what the date and time inputs are entered in, so a time typed and the same
 * time shown stay one time. Where a page states "office time" it pins this.
 */
let displayTimeZone = DEFAULT_PROJECT_TIME_ZONE;
export function setDisplayTimeZone(timeZone: string): void {
  displayTimeZone = timeZone;
}
export function getDisplayTimeZone(): string {
  return displayTimeZone;
}
/** The display zone as the reader says it, in their language: "Central Time", "heure de l'Est". */
export function displayTimeZoneLabel(): string {
  return timeZoneLabel(displayTimeZone, displayLocale);
}
/** The placeholder for a value that is absent — not zero, not blank. */
export const NO_VALUE = "—";

const numberFormats = new Map<string, Intl.NumberFormat>();
const dateFormats = new Map<string, Intl.DateTimeFormat>();
const relativeFormats = new Map<string, Intl.RelativeTimeFormat>();

function cached<F>(cache: Map<string, F>, locale: string | undefined, options: object | undefined, make: () => F): F {
  const key = `${locale ?? ""}|${JSON.stringify(options ?? {})}`;
  let f = cache.get(key);
  if (!f) {
    f = make();
    cache.set(key, f);
  }
  return f;
}

/** A shared Intl.NumberFormat for these options. */
export function numberFormat(options?: Intl.NumberFormatOptions, locale: string = LOCALE): Intl.NumberFormat {
  return cached(numberFormats, locale, options, () => new Intl.NumberFormat(locale, options));
}

/** A shared Intl.DateTimeFormat for these options. */
export function dateFormat(options?: Intl.DateTimeFormatOptions, locale: string = LOCALE): Intl.DateTimeFormat {
  return cached(dateFormats, locale, options, () => new Intl.DateTimeFormat(locale, options));
}

/** A shared Intl.RelativeTimeFormat for these options. */
export function relativeTimeFormat(options?: Intl.RelativeTimeFormatOptions, locale: string = LOCALE): Intl.RelativeTimeFormat {
  return cached(relativeFormats, locale, options, () => new Intl.RelativeTimeFormat(locale, options));
}

/** For tests: how many formatters have been built so far. */
export function formatterCacheSize(): number {
  return numberFormats.size + dateFormats.size + relativeFormats.size;
}

// ─── Numbers ────────────────────────────────────────────────────────────────

type Numberish = number | string | null | undefined;

/** `null` unless the input reads as a finite number. "" and null are missing data, not 0. */
export function toFinite(value: Numberish): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(String(value).replace(/[$,\s]/g, ""));
  return Number.isFinite(n) ? n : null;
}

/** A count, grouped: `1,240`. */
export function formatNumber(value: Numberish, options?: Intl.NumberFormatOptions): string {
  const n = toFinite(value);
  return n === null ? NO_VALUE : numberFormat(options, displayLocale).format(n);
}

/**
 * THE currency formatter — the only one in the app (lib/formatNumber's
 * `money()` was folded into it on 2026-10-27; tests/currencyFormatter.test.ts
 * keeps it that way). The open project's dollar (CAD, or USD on a Florida or
 * Texas project), in the reader's locale (INTL_LOCALE, set by
 * LanguageProvider): "$1,249,900", "1 249 900 $". Whole dollars by default,
 * which is how every price and total in this app is stored and shown;
 * `cents: true` prints to the cent. `fromCents: true` when the stored value
 * is an integer number of cents. `compact: true` for a summary figure where
 * every dollar does not fit: "$8.7M", "$330K" ("7,4 M$" in French). A
 * missing figure is the em dash, never $0.
 */
export function formatCurrency(value: Numberish, opts: { cents?: boolean; fromCents?: boolean; compact?: boolean } = {}): string {
  const n = toFinite(value);
  if (n === null) return NO_VALUE;
  const amount = opts.fromCents ? n / 100 : n;
  if (opts.compact) {
    return numberFormat({
      style: "currency",
      currency: displayCurrency,
      currencyDisplay: "narrowSymbol",
      notation: "compact",
      maximumFractionDigits: Math.abs(amount) >= 1_000_000 ? 1 : 0,
    }, displayLocale).format(amount);
  }
  const digits = opts.cents ? 2 : 0;
  return numberFormat({
    style: "currency",
    currency: displayCurrency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }, displayLocale).format(amount);
}

/**
 * A percentage. The app stores rates as 0–100 (openRate 42 means 42%), so that
 * is the default; `ratio: true` for a 0–1 fraction. Never guesses which it was
 * given — a function that silently multiplied would print a plausible wrong number.
 */
export function formatPercent(value: Numberish, opts: { decimals?: number; ratio?: boolean } = {}): string {
  const n = toFinite(value);
  if (n === null) return NO_VALUE;
  const d = opts.decimals ?? 0;
  return numberFormat({ style: "percent", minimumFractionDigits: d, maximumFractionDigits: d }, displayLocale).format(opts.ratio ? n : n / 100);
}

// ─── Dates ──────────────────────────────────────────────────────────────────

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A date-only value ("2026-09-04" — a firm date, a due date, a closing) is a
 * CALENDAR DAY, not an instant. `new Date("2026-09-04")` is UTC midnight, which
 * renders as September 3 anywhere west of Greenwich — every such date in the
 * console would read a day early. Anchored at noon UTC and formatted in UTC,
 * the day survives every time zone. Anything with a time is a real instant.
 */
function parseDateish(d: string | number | Date): { date: Date; dayOnly: boolean } | null {
  if (d instanceof Date) return Number.isNaN(d.getTime()) ? null : { date: d, dayOnly: false };
  if (typeof d === "number") return Number.isFinite(d) ? { date: new Date(d), dayOnly: false } : null;
  const s = d.trim();
  const m = DATE_ONLY.exec(s);
  if (m) return { date: new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], 12)), dayOnly: true };
  const t = Date.parse(s);
  return Number.isNaN(t) ? null : { date: new Date(t), dayOnly: false };
}

export type DateStyle = "short" | "medium" | "long" | "numeric" | "monthDay" | "monthYear";

const DATE_STYLES: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  /** Sep 4 */
  monthDay: { month: "short", day: "numeric" },
  /** Sep 2026 */
  monthYear: { month: "short", year: "numeric" },
  /** Sep 4, 2026 */
  medium: { year: "numeric", month: "short", day: "numeric" },
  /** September 4, 2026 */
  long: { year: "numeric", month: "long", day: "numeric" },
  /** 2026-09-04 (en-CA's numeric order is ISO) */
  numeric: { year: "numeric", month: "2-digit", day: "2-digit" },
  /** The locale's own short form (en-CA: 2026-09-04; fr-CA: 2026-09-04; en-US: 9/4/26) */
  short: { dateStyle: "short" },
};

/**
 * A day: `formatDate("2026-09-04")` → "Sep 4, 2026". An instant is shown in the
 * viewer's zone unless `timeZone` says otherwise; a date-only value keeps its
 * calendar day everywhere.
 */
export function formatDate(
  value: string | number | Date | null | undefined,
  style: DateStyle | Intl.DateTimeFormatOptions = "medium",
  opts: { timeZone?: string } = {},
): string {
  if (value === null || value === undefined || value === "") return NO_VALUE;
  const parsed = parseDateish(value);
  if (!parsed) return NO_VALUE;
  const base = typeof style === "string" ? DATE_STYLES[style] : style;
  const timeZone = parsed.dayOnly ? "UTC" : opts.timeZone;
  const locale = style === "numeric" ? LOCALE : displayLocale;
  return dateFormat(timeZone ? { ...base, timeZone } : base, locale).format(parsed.date);
}

/** An instant with its time: "Sep 4, 2026, 2:15 p.m.". */
export function formatDateTime(value: string | number | Date | null | undefined, opts: { timeZone?: string } = {}): string {
  if (value === null || value === undefined || value === "") return NO_VALUE;
  const parsed = parseDateish(value);
  if (!parsed) return NO_VALUE;
  const o: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
  return dateFormat(opts.timeZone ? { ...o, timeZone: opts.timeZone } : o, displayLocale).format(parsed.date);
}

/** Just the time: "2:15 p.m.". */
export function formatTime(value: string | number | Date | null | undefined, opts: { timeZone?: string } = {}): string {
  if (value === null || value === undefined || value === "") return NO_VALUE;
  const parsed = parseDateish(value);
  if (!parsed || parsed.dayOnly) return NO_VALUE;
  const o: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };
  return dateFormat(opts.timeZone ? { ...o, timeZone: opts.timeZone } : o, displayLocale).format(parsed.date);
}

const RELATIVE_STEPS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["second", 60],
  ["minute", 60],
  ["hour", 24],
  ["day", 7],
  ["week", 4.34524],
  ["month", 12],
  ["year", Number.POSITIVE_INFINITY],
];

/**
 * "3 minutes ago", "yesterday", "in 2 weeks". `numeric: "auto"` so the
 * neighbours read as words; anything under a minute is "now".
 */
export function formatRelative(value: string | number | Date | null | undefined, now: Date = new Date()): string {
  if (value === null || value === undefined || value === "") return NO_VALUE;
  const parsed = parseDateish(value);
  if (!parsed) return NO_VALUE;
  let delta = (parsed.date.getTime() - now.getTime()) / 1000;
  const rtf = relativeTimeFormat({ numeric: "auto" }, displayLocale);
  if (Math.abs(delta) < 45) return rtf.format(0, "second");
  for (const [unit, size] of RELATIVE_STEPS) {
    if (Math.abs(delta) < size) return rtf.format(Math.round(delta), unit);
    delta /= size;
  }
  return rtf.format(Math.round(delta), "year");
}

/**
 * The date in Toronto as YYYY-MM-DD, for the platform's own pages: platform
 * reports, usage, the user log. `toISOString().slice(0, 10)` is the UTC day,
 * which after 8 p.m. Eastern names tomorrow.
 */
export function torontoYmd(at: Date = new Date()): string {
  return zonedYmd(at, PLATFORM_TIME_ZONE);
}

/**
 * The date in the open project's zone as YYYY-MM-DD — the day a project's
 * file is named for, "today" on its date pickers and the day "overdue" is
 * measured against. Toronto's outside a project.
 */
export function projectYmd(at: Date = new Date()): string {
  return zonedYmd(at, displayTimeZone);
}
